/**
 * GolferDiscovery - PS Golfers browsing
 * Find golfers nearby, chat, build your crew
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin,
  Flag,
  Calendar,
  MessageCircle,
  UserPlus,
  Lock,
  Coins,
  ChevronLeft,
  ChevronRight,
  X,
  Eye,
  SlidersHorizontal,
  ArrowLeft,
  Circle,
  Gift,
  Target,
  Users,
  Trophy,
  ChevronRight as ChevronRightIcon,
  Zap,
  Rocket,
  Clock,
  Sparkles,
  EyeIcon,
  UserCheck,
  Heart,
  Star,
  Check,
  Search,
  Medal,
  Navigation
} from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import confetti from "canvas-confetti";
import { LocationPermissionBanner, LocationDeniedBanner, LocationStatusIndicator } from "./LocationPermissionBanner";
import { useLocationContext } from "../hooks/LocationProvider";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Coin costs for actions
const COIN_COSTS = {
  VIEW_PROFILE: 0,
  SEND_MESSAGE: 0,
  SEND_BUDDY_REQUEST: 0,
  SEE_WHO_VIEWED: 0,
  BOOST_PROFILE: 50,
  UNLOCK_AREA_SESSION: 0,
  LIKE: 0,
  SUPER_LIKE: 0,
  SEE_LIKES: 0
};

// Earn Coins Popup Modal - Shows when user lacks coins
const EarnCoinsModal = ({ isOpen, onClose, userCoins, neededCoins, onNavigate }) => {
  const earningMethods = [
    { icon: Target, label: "Log a round", reward: "+10", action: () => onNavigate('/log-score') },
    { icon: Users, label: "Invite friend (verified)", reward: "+50", action: () => onNavigate('/golf-coins?tab=earn') },
    { icon: Gift, label: "Complete profile", reward: "+25", action: () => onNavigate('/profile') },
    { icon: Trophy, label: "First handicap (3 rounds)", reward: "+100", action: null },
    { icon: Calendar, label: "Daily login streak", reward: "+5-15", action: null },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="ps-themed-dialog ps-theme-panel max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-white text-center flex items-center justify-center gap-2">
            <Coins className="w-6 h-6 text-amber-400" />
            Earn Golf Coins
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          {/* Current Balance */}
          <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg mb-4">
            <span className="text-gray-400">Your balance</span>
            <span className="font-bold text-amber-400">{userCoins} coins</span>
          </div>
          
          {neededCoins > 0 && (
            <p className="text-center text-red-400 text-sm mb-4">
              You need {neededCoins} more coins for this action
            </p>
          )}
          
          {/* Earning Methods */}
          <div className="space-y-2">
            {earningMethods.map((method, i) => (
              <button
                key={i}
                onClick={method.action || undefined}
                disabled={!method.action}
                className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                  method.action 
                    ? 'bg-gray-800 hover:bg-gray-700 cursor-pointer' 
                    : 'bg-gray-800/50 cursor-default'
                }`}
              >
                <div className="flex items-center gap-3">
                  <method.icon className="w-5 h-5 text-emerald-400" />
                  <span className="text-white text-sm">{method.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-amber-400 font-semibold">{method.reward}</span>
                  {method.action && <ChevronRightIcon className="w-4 h-4 text-gray-500" />}
                </div>
              </button>
            ))}
          </div>
        </div>
        
        <DialogFooter className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1 border-gray-700 text-gray-400">
            Close
          </Button>
          <Button 
            onClick={() => onNavigate('/golf-coins?tab=buy')}
            className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 text-white"
          >
            <Coins className="w-4 h-4 mr-2" />
            Buy Coins
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Blurred profile card (teaser) - Lightened blur effect
const BlurredProfileCard = ({ golfer, onUnlock, userCoins, isCarousel = false }) => {
  const [imgError, setImgError] = useState(false);
  
  // Get initials for fallback
  const initials = golfer.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'G';
  
  return (
    <div className={`relative bg-gray-900 rounded-2xl overflow-hidden border ${golfer.is_boosted ? 'border-amber-500/50 ring-2 ring-amber-500/20' : 'border-gray-800'} ${isCarousel ? 'h-full' : ''}`}>
      {/* Boosted Badge */}
      {golfer.is_boosted && (
        <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-r from-amber-500 to-orange-500 py-1.5 px-3 flex items-center justify-center gap-2">
          <Zap className="w-4 h-4 text-white fill-white" />
          <span className="text-white text-xs font-bold uppercase tracking-wide">Boosted Profile</span>
          <Zap className="w-4 h-4 text-white fill-white" />
        </div>
      )}
      
      {/* Lightened Blur Image */}
      <div className={`ps-fixed-contrast relative h-80 ${golfer.is_boosted ? 'mt-8' : ''}`}>
        {/* Background blur layer - LIGHTENED */}
        {golfer.picture && !imgError ? (
          <img 
            src={golfer.picture}
            alt=""
            onError={() => setImgError(true)}
            className="absolute inset-0 w-full h-full object-cover scale-110"
            style={{ filter: 'blur(12px) brightness(0.85)' }}
          />
        ) : (
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(135deg, #2a2a2a 0%, #3d5a4a 50%, #2a2a2a 100%)',
              filter: 'blur(8px)'
            }}
          />
        )}
        
        {/* Lighter overlay */}
        <div className="absolute inset-0 bg-black/30" />
        
        {/* Online Indicator */}
        {golfer.is_online && (
          <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-emerald-500/90 text-white text-xs flex items-center gap-1.5 z-10">
            <Circle className="w-2 h-2 fill-white text-white animate-pulse" />
            Online
          </div>
        )}
        
        {/* Lock Overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="w-20 h-20 rounded-full bg-pink-500/20 border-2 border-pink-500/50 flex items-center justify-center mb-4">
            <span className="text-4xl">⛳️</span>
          </div>
          <p className="text-white font-semibold text-lg mb-1">Want to Connect?</p>
          <p className="text-gray-400 text-sm mb-4">Send a request to view & chat</p>
          
          <Button
            onClick={() => onUnlock(golfer)}
            className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white px-6"
            data-testid="unlock-profile-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Connect
          </Button>
        </div>
      </div>
      
      {/* Teaser Info */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-white font-semibold text-lg">
              {golfer.name?.split(' ')[0] || 'Golfer'}
            </h3>
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <MapPin className="w-3 h-3" />
              <span>{golfer.location || golfer.city || `${golfer.distance || '< 5'} miles away`}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-emerald-400 font-bold text-xl">
              {golfer.handicap != null ? golfer.handicap.toFixed(1) : '—'}
            </div>
            <div className="text-gray-500 text-xs">Handicap</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Full profile card (unlocked) - Simplified with tap to view profile
const ProfileCard = ({ golfer, onViewProfile, isCarousel = false }) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [imgError, setImgError] = useState(false);
  const [showEmojiMenu, setShowEmojiMenu] = useState(false);
  
  const photos = golfer.photos?.length > 0 ? golfer.photos : (golfer.picture ? [golfer.picture] : []);
  const initials = golfer.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'G';
  
  const nextPhoto = (e) => {
    e.stopPropagation();
    if (photos.length > 1) {
      setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
      setImgError(false);
    }
  };
  
  const prevPhoto = (e) => {
    e.stopPropagation();
    if (photos.length > 1) {
      setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
      setImgError(false);
    }
  };

  // Golf emoji reactions for quick interactions
  const golfEmojis = [
    { emoji: '⛳', label: 'Flag', action: 'flag' },
    { emoji: '🏌️', label: 'Golfer', action: 'wave' },
    { emoji: '🔥', label: 'Fire', action: 'fire' },
    { emoji: '👏', label: 'Clap', action: 'clap' },
    { emoji: '🎯', label: 'Target', action: 'target' },
  ];

  const handleEmojiSelect = (emoji) => {
    // This could trigger a notification to the other user
    toast.success(`Sent ${emoji.emoji} to ${golfer.name?.split(' ')[0]}!`);
    setShowEmojiMenu(false);
  };

  return (
    <div className={`relative bg-gray-900 rounded-2xl overflow-hidden border ${golfer.is_boosted ? 'border-amber-500/50 ring-2 ring-amber-500/20' : 'border-gray-800'} ${isCarousel ? 'h-full flex flex-col' : ''}`}>
      {/* Boosted Badge */}
      {golfer.is_boosted && (
        <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-r from-amber-500 to-orange-500 py-1 px-3 flex items-center justify-center gap-2">
          <Zap className="w-3 h-3 text-white fill-white" />
          <span className="text-white text-xs font-bold uppercase tracking-wide">Boosted</span>
          <Zap className="w-3 h-3 text-white fill-white" />
        </div>
      )}
      
      {/* Photo Area - Tappable to view full profile */}
      <div 
        className={`ps-fixed-contrast relative ${isCarousel ? 'h-[45%] min-h-[180px]' : 'h-72'} ${golfer.is_boosted ? 'mt-6' : ''} cursor-pointer`}
        onClick={() => onViewProfile(golfer)}
        data-testid="profile-photo-tap"
      >
        {photos.length > 0 && !imgError ? (
          <img 
            src={photos[currentPhotoIndex]} 
            alt={golfer.name}
            onError={() => setImgError(true)}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-emerald-900/50 to-gray-900 flex items-center justify-center">
            <div className="w-24 h-24 rounded-full bg-emerald-500/20 border-2 border-emerald-500/30 flex items-center justify-center">
              <span className="text-4xl font-bold text-emerald-400/60">{initials}</span>
            </div>
          </div>
        )}
        
        {/* Tap hint overlay */}
        <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center">
          <div className="opacity-0 hover:opacity-100 transition-opacity bg-black/60 px-4 py-2 rounded-full">
            <span className="text-white text-sm">Tap to view profile</span>
          </div>
        </div>
        
        {/* Photo Navigation */}
        {photos.length > 1 && !imgError && (
          <>
            <button 
              onClick={prevPhoto}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 z-10"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button 
              onClick={nextPhoto}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 z-10"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 rounded-full z-10">
              <span className="text-white text-xs font-medium">
                {currentPhotoIndex + 1} / {photos.length}
              </span>
            </div>
          </>
        )}
        
        {/* Online Status */}
        {golfer.is_online && (
          <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-emerald-500/90 text-white text-sm flex items-center gap-1 z-10">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
            Online now
          </div>
        )}
      </div>
      
      {/* Profile Info - compact for mobile */}
      <div className={`p-4 ${isCarousel ? 'flex-1 overflow-y-auto' : ''}`}>
        {/* Name & Basic Info with Emoji Menu */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0" onClick={() => onViewProfile(golfer)} style={{cursor: 'pointer'}}>
            <h2 className="text-xl font-bold text-white truncate">{golfer.name}</h2>
            {golfer.username && (
              <p className="text-gray-400 text-sm">@{golfer.username}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-2xl font-bold text-emerald-400">
                {golfer.handicap != null ? golfer.handicap.toFixed(1) : '—'}
              </div>
              <div className="text-gray-500 text-xs">Handicap</div>
            </div>
            
            {/* Golf Emoji Menu Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowEmojiMenu(!showEmojiMenu)}
                className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
                data-testid="emoji-menu-btn"
              >
                <span className="text-lg">⛳</span>
              </button>
              
              {showEmojiMenu && (
                <div className="absolute right-0 top-full mt-2 bg-gray-800 border border-gray-700 rounded-xl p-2 shadow-xl z-30 min-w-[200px]">
                  <p className="text-gray-400 text-xs px-2 mb-2">Send a reaction</p>
                  <div className="flex flex-wrap gap-1">
                    {golfEmojis.map((item) => (
                      <button
                        key={item.action}
                        onClick={() => handleEmojiSelect(item)}
                        className="p-2 rounded-lg hover:bg-gray-700 transition-colors flex flex-col items-center"
                        data-testid={`emoji-${item.action}`}
                      >
                        <span className="text-2xl">{item.emoji}</span>
                        <span className="text-gray-500 text-xs mt-0.5">{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Stats Row - compact */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-gray-800/50 rounded-lg p-2 text-center">
            <MapPin className="w-4 h-4 text-emerald-400 mx-auto mb-0.5" />
            <p className="text-white font-medium text-xs truncate">{golfer.location || golfer.city || 'Local'}</p>
            <p className={`text-xs ${golfer.distance_accurate ? 'text-emerald-400' : golfer.distance === '?' ? 'text-gray-600' : 'text-gray-400'}`}>
              {golfer.distance === '?' ? 'Distance unknown' : `${golfer.distance} mi`}
              {golfer.distance_accurate && <Navigation className="w-2.5 h-2.5 inline ml-0.5" />}
            </p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-2 text-center">
            <Flag className="w-4 h-4 text-emerald-400 mx-auto mb-0.5" />
            <p className="text-white font-medium text-xs">{golfer.rounds_played || 0}</p>
            <p className="text-gray-500 text-xs">Rounds</p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-2 text-center">
            <Calendar className="w-4 h-4 text-emerald-400 mx-auto mb-0.5" />
            <p className="text-white font-medium text-xs capitalize">{golfer.availability || 'Flexible'}</p>
            <p className="text-gray-500 text-xs">Plays</p>
          </div>
        </div>
        
        {/* Home Course */}
        {golfer.home_course && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 mb-3">
            <p className="text-emerald-400 text-xs font-medium">Home Course</p>
            <p className="text-white text-sm">{golfer.home_course}</p>
          </div>
        )}
        
        {/* Bio */}
        {golfer.bio && (
          <p className="text-gray-300 text-sm line-clamp-2">{golfer.bio}</p>
        )}
      </div>
    </div>
  );
};

// ============ PROFILE CAROUSEL COMPONENT ============
const ProfileCarousel = ({ 
  golfers, 
  onViewProfile,
  onDismiss
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [swipeDirection, setSwipeDirection] = useState(null); // 'horizontal' or 'vertical'
  const audioContextRef = useRef(null);
  const carouselRef = useRef(null);

  // Play swoosh sound
  const playSwoosh = useCallback(() => {
    try {
      const audioContext = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      // Resume audio context if suspended (required for mobile browsers)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(250, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(60, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.06, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (e) {}
  }, []);

  // Total items = golfers.length (leaderboard removed from carousel)
  const totalItems = golfers.length;

  const goToCard = useCallback((index) => {
    // Infinite carousel - wrap around at ends
    let newIndex = index;
    if (index < 0) {
      newIndex = totalItems - 1; // Wrap to last
    } else if (index >= totalItems) {
      newIndex = 0; // Wrap to first
    }
    if (newIndex !== currentIndex) {
      playSwoosh();
      setCurrentIndex(newIndex);
    }
  }, [currentIndex, totalItems, playSwoosh]);

  // Touch handlers - soft lock vertical scroll, prioritize horizontal swipes
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    setDragOffset(0);
    setSwipeDirection(null);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    const deltaX = e.touches[0].clientX - dragStart.x;
    const deltaY = e.touches[0].clientY - dragStart.y;
    
    // Determine swipe direction on first significant movement
    if (!swipeDirection) {
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      
      if (absX > 10 || absY > 10) {
        // Only allow vertical if it's a strong vertical swipe (3x more than horizontal)
        if (absY > absX * 3 && absY > 80) {
          setSwipeDirection('vertical');
        } else {
          setSwipeDirection('horizontal');
          // Prevent page scroll when swiping horizontally
          e.preventDefault();
        }
      }
    }
    
    // Only update horizontal offset if swiping horizontally
    if (swipeDirection === 'horizontal' || !swipeDirection) {
      setDragOffset(deltaX);
      if (swipeDirection === 'horizontal') {
        e.preventDefault();
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    const threshold = 50;
    
    // Only navigate if horizontal swipe
    if (swipeDirection !== 'vertical') {
      if (dragOffset < -threshold) {
        goToCard(currentIndex + 1);
      } else if (dragOffset > threshold) {
        goToCard(currentIndex - 1);
      }
    }
    
    setIsDragging(false);
    setDragOffset(0);
    setSwipeDirection(null);
  };

  // Mouse handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart(e.clientX);
    setDragOffset(0);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setDragOffset(e.clientX - dragStart);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    const threshold = 60;
    if (dragOffset < -threshold) {
      // Swipe left - go to next (or wrap to first)
      goToCard(currentIndex + 1);
    } else if (dragOffset > threshold) {
      // Swipe right - go to previous (or wrap to last)
      goToCard(currentIndex - 1);
    }
    setIsDragging(false);
    setDragOffset(0);
  };

  // Keyboard navigation for infinite carousel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        goToCard(currentIndex + 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        goToCard(currentIndex - 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, goToCard]);

  if (golfers.length === 0) return null;

  // Create carousel items: only golfer profiles (leaderboard is separate)
  const carouselItems = golfers.map(g => ({ type: 'profile', id: g.user_id, golfer: g }));

  return (
    <div className="space-y-2">
      {/* Tap area above carousel - dismisses to home */}
      <div 
        className="h-2 cursor-pointer" 
        onClick={onDismiss}
        data-testid="carousel-dismiss-top"
      />
      
      {/* Carousel container - mobile optimized height */}
      <div
        ref={carouselRef}
        className="relative h-[calc(100vh-200px)] min-h-[400px] max-h-[600px] select-none touch-pan-y"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ 
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: swipeDirection === 'horizontal' ? 'none' : 'pan-y'
        }}
      >
        {carouselItems.map((item, index) => {
          const offset = index - currentIndex;
          
          if (Math.abs(offset) > 1) return null;
          
          const translateX = isDragging 
            ? (offset * 90) + (dragOffset / 4)
            : offset * 90;
          const scale = offset === 0 ? 1 : 0.85;
          const opacity = offset === 0 ? 1 : 0.3;
          const zIndex = 10 - Math.abs(offset);
          
          return (
            <div
              key={item.id}
              className={`
                absolute inset-x-0 top-0 h-full
                transition-all duration-300 ease-out
                ${isDragging ? 'transition-none' : ''}
              `}
              style={{
                transform: `translateX(${translateX}%) scale(${scale})`,
                opacity: Math.max(0, opacity),
                zIndex,
                pointerEvents: offset === 0 ? 'auto' : 'none'
              }}
            >
              <ProfileCard
                golfer={item.golfer}
                onViewProfile={onViewProfile}
                isCarousel={true}
              />
            </div>
          );
        })}
      </div>

      {/* Swipe hint - also tappable to dismiss */}
      <div 
        className="flex items-center justify-center gap-3 text-gray-400 text-sm cursor-pointer py-2"
        onClick={onDismiss}
        data-testid="carousel-dismiss-bottom"
      >
        <ChevronLeft className="w-5 h-5 opacity-70" />
        <span>Swipe to discover golfers</span>
        <ChevronRight className="w-5 h-5 opacity-70" />
      </div>
    </div>
  );
};

// Coin Gate Modal - Simplified without showing costs in buttons
const CoinGateModal = ({ isOpen, onClose, action, cost, userCoins, onConfirm, onShowEarnCoins }) => {
  const canAfford = userCoins >= cost;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="ps-themed-dialog ps-theme-panel max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-white text-center">
            {canAfford ? 'Confirm Action' : 'Need More Coins'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Coins className="w-8 h-8 text-amber-400" />
            <span className="text-3xl font-bold text-amber-400">{cost}</span>
            <span className="text-gray-400">coins</span>
          </div>
          
          <p className="text-center text-gray-400 mb-4">
            {action}
          </p>
          
          <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg mb-4">
            <span className="text-gray-400">Your balance</span>
            <span className={`font-bold ${canAfford ? 'text-emerald-400' : 'text-red-400'}`}>
              {userCoins} coins
            </span>
          </div>
          
          {!canAfford && (
            <p className="text-center text-red-400 text-sm mb-4">
              You need {cost - userCoins} more coins
            </p>
          )}
        </div>
        
        <DialogFooter className="flex gap-3">
          {canAfford ? (
            <>
              <Button variant="outline" onClick={onClose} className="flex-1 border-gray-700 text-gray-400">
                Cancel
              </Button>
              <Button 
                onClick={onConfirm}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white"
              >
                Confirm
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} className="flex-1 border-gray-700 text-gray-400">
                Cancel
              </Button>
              <Button 
                onClick={onShowEarnCoins}
                className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 text-white"
              >
                <Coins className="w-4 h-4 mr-2" />
                Earn Coins
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Discovery Page
const GolferDiscovery = ({ user }) => {
  const navigate = useNavigate();
  const [golfers, setGolfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userCoins, setUserCoins] = useState(0);
  const [sessionUnlocked, setSessionUnlocked] = useState(true);  // Always unlocked
  const [selectedGolfer, setSelectedGolfer] = useState(null);
  const [unlockedProfiles, setUnlockedProfiles] = useState(new Set());  // Not used anymore - all profiles visible
  
  // Location tracking for real distances
  const { hasLocation, isTracking, promptForLocation } = useLocationContext();
  const [showLocationBanner, setShowLocationBanner] = useState(true);
  
  // Boost state
  const [boostStatus, setBoostStatus] = useState({ is_boosted: false, boost_available: true });
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [boostLoading, setBoostLoading] = useState(false);
  
  // Who Viewed You state
  const [viewsData, setViewsData] = useState({ view_count: 0, is_unlocked: false, hours_remaining: 0 });
  const [showViewersModal, setShowViewersModal] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [viewersLoading, setViewersLoading] = useState(false);
  
  // Likes state
  const [likesData, setLikesData] = useState({ like_count: 0, super_like_count: 0, is_unlocked: false, hours_remaining: 0 });
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [likers, setLikers] = useState([]);
  const [likersLoading, setLikersLoading] = useState(false);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [matchedUser, setMatchedUser] = useState(null);
  const [likedProfiles, setLikedProfiles] = useState(new Set());
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filterDistance, setFilterDistance] = useState('all');
  const [filterHandicap, setFilterHandicap] = useState('all');
  const [filterGender, setFilterGender] = useState('all');
  const [filterAge, setFilterAge] = useState('all');
  
  // Full profile view state
  const [selectedProfile, setSelectedProfile] = useState(null);
  
  // Coin gate modal state
  const [coinModal, setCoinModal] = useState({
    isOpen: false,
    action: '',
    cost: 0,
    onConfirm: null
  });
  
  // Earn coins modal state
  const [showEarnCoins, setShowEarnCoins] = useState(false);
  const [neededCoins, setNeededCoins] = useState(0);
  
  // Low coins warning (shown once per session when balance drops below threshold)
  const [lowCoinWarningShown, setLowCoinWarningShown] = useState(false);
  const LOW_COIN_THRESHOLD = 10;
  
  // Area unlock modal
  const [showAreaUnlock, setShowAreaUnlock] = useState(false);
  
  // Filter golfers based on selected criteria
  const filteredGolfers = golfers.filter(golfer => {
    // Distance filter - use distance_miles for numeric comparison
    if (filterDistance !== 'all') {
      const maxDistance = parseInt(filterDistance);
      // Use distance_miles if available, otherwise try to parse the distance string
      const golferDistance = golfer.distance_miles || parseFloat(golfer.distance?.replace(/[^0-9.]/g, '')) || 9999;
      if (golferDistance > maxDistance) return false;
    }
    
    // Handicap filter
    if (filterHandicap !== 'all') {
      const handicap = golfer.handicap;
      if (handicap == null) return false;
      
      switch(filterHandicap) {
        case 'scratch':
          if (handicap > 5) return false;
          break;
        case 'low':
          if (handicap < 6 || handicap > 12) return false;
          break;
        case 'mid':
          if (handicap < 13 || handicap > 20) return false;
          break;
        case 'high':
          if (handicap < 21) return false;
          break;
        default:
          break;
      }
    }
    
    // Gender filter
    if (filterGender !== 'all') {
      const golferGender = golfer.gender?.toLowerCase() || '';
      if (golferGender !== filterGender) return false;
    }
    
    // Age filter
    if (filterAge !== 'all') {
      const age = golfer.age;
      if (age == null) return true; // Show if age not set
      
      switch(filterAge) {
        case '18-29':
          if (age < 18 || age > 29) return false;
          break;
        case '30-39':
          if (age < 30 || age > 39) return false;
          break;
        case '40-49':
          if (age < 40 || age > 49) return false;
          break;
        case '50-59':
          if (age < 50 || age > 59) return false;
          break;
        case '60+':
          if (age < 60) return false;
          break;
        default:
          break;
      }
    }
    
    return true;
  });

  const fetchGolfers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/golfers/discover`);
      setGolfers(response.data.golfers || []);
    } catch (error) {
      console.error("Error fetching golfers:", error);
      setGolfers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserCoins = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/golf-coins/balance`);
      setUserCoins(response.data?.balance || 0);
    } catch (error) {
      console.error("Error fetching coins:", error);
    }
  }, []);

  const fetchBoostStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/boost/status`);
      setBoostStatus(response.data);
    } catch (error) {
      console.error("Error fetching boost status:", error);
    }
  }, []);

  const fetchViewsData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/profile/views/count`);
      setViewsData(response.data);
    } catch (error) {
      console.error("Error fetching views data:", error);
    }
  }, []);

  const fetchLikesData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/likes/received/count`);
      setLikesData(response.data);
    } catch (error) {
      console.error("Error fetching likes data:", error);
    }
  }, []);

  const fetchLikedProfiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/likes/sent`);
      const likedIds = new Set(response.data.liked_users?.map(u => u.user_id) || []);
      setLikedProfiles(likedIds);
    } catch (error) {
      console.error("Error fetching liked profiles:", error);
    }
  }, []);

  useEffect(() => {
    fetchGolfers();
    fetchUserCoins();
    fetchBoostStatus();
    fetchViewsData();
    fetchLikesData();
    fetchLikedProfiles();
    
    const unlocked = sessionStorage.getItem('discovery_session_unlocked');
    if (unlocked === 'true') {
      setSessionUnlocked(true);
    }
  }, [fetchGolfers, fetchUserCoins, fetchBoostStatus, fetchViewsData, fetchLikesData, fetchLikedProfiles]);

  // Refetch golfers when location is enabled to get accurate distances
  useEffect(() => {
    if (hasLocation && !loading) {
      // Small delay to ensure backend has the updated location
      const timer = setTimeout(() => {
        fetchGolfers();
        toast.success('📍 Distances updated with your location!');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [hasLocation]); // Only re-run when hasLocation changes

  // Low coin warning - show once per session when balance drops below threshold
  useEffect(() => {
    if (userCoins > 0 && userCoins < LOW_COIN_THRESHOLD && !lowCoinWarningShown && sessionUnlocked) {
      setLowCoinWarningShown(true);
      toast.warning(`Running low on coins! Only ${userCoins} left.`, {
        duration: 4000,
        action: {
          label: 'Get More',
          onClick: () => navigate('/golf-coins')
        }
      });
    }
  }, [userCoins, lowCoinWarningShown, sessionUnlocked, navigate]);

  // Like handler
  const handleLike = async (golfer, isSuper = false) => {
    if (likedProfiles.has(golfer.user_id)) {
      toast.info('You already liked this golfer!');
      return;
    }
    
    const cost = isSuper ? COIN_COSTS.SUPER_LIKE : COIN_COSTS.LIKE;
    
    if (userCoins < cost) {
      setNeededCoins(cost - userCoins);
      setShowEarnCoins(true);
      return;
    }
    
    // Silent deduction - no popup confirmation
    try {
      const response = await axios.post(`${API}/likes/send?target_user_id=${golfer.user_id}&is_super=${isSuper}`);
      
      if (response.data.success) {
        setUserCoins(response.data.new_balance);
        setLikedProfiles(prev => new Set([...prev, golfer.user_id]));
        
        if (response.data.is_match) {
          // IT'S A MATCH! - This one we DO show!
          setMatchedUser(response.data.matched_user || golfer);
          setShowMatchModal(true);
          
          // Confetti celebration!
          confetti({
            particleCount: 150,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#ec4899', '#f43f5e', '#f97316', '#eab308']
          });
        }
        // No toast for regular likes - keep it silent
      } else if (response.data.already_liked) {
        // Silent - already liked
      }
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('coins')) {
        setNeededCoins(cost);
        setShowEarnCoins(true);
      }
      // Silent fail for other errors
    }
  };

  // Unlock Likes handler
  const handleUnlockLikes = async () => {
    if (userCoins < COIN_COSTS.SEE_LIKES) {
      setShowLikesModal(false);
      setNeededCoins(COIN_COSTS.SEE_LIKES - userCoins);
      setShowEarnCoins(true);
      return;
    }
    
    setLikersLoading(true);
    try {
      const response = await axios.post(`${API}/likes/received/unlock`);
      if (response.data.success) {
        setUserCoins(response.data.new_balance);
        setLikesData(prev => ({
          ...prev,
          is_unlocked: true,
          hours_remaining: response.data.hours_remaining
        }));
        
        // Fetch the likers list
        const likersResponse = await axios.get(`${API}/likes/received/list`);
        setLikers(likersResponse.data.likers || []);
        toast.success('Likes unlocked! See who\'s interested in you.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock likes');
    } finally {
      setLikersLoading(false);
    }
  };

  const handleOpenLikesModal = async () => {
    setShowLikesModal(true);
    if (likesData.is_unlocked) {
      setLikersLoading(true);
      try {
        const response = await axios.get(`${API}/likes/received/list`);
        setLikers(response.data.likers || []);
      } catch (error) {
        console.error("Error fetching likers:", error);
      } finally {
        setLikersLoading(false);
      }
    }
  };

  const handleUnlockViewers = async () => {
    if (userCoins < COIN_COSTS.SEE_WHO_VIEWED) {
      setShowViewersModal(false);
      setNeededCoins(COIN_COSTS.SEE_WHO_VIEWED - userCoins);
      setShowEarnCoins(true);
      return;
    }
    
    setViewersLoading(true);
    try {
      const response = await axios.post(`${API}/profile/views/unlock`);
      if (response.data.success) {
        setUserCoins(response.data.new_balance);
        setViewsData(prev => ({
          ...prev,
          is_unlocked: true,
          hours_remaining: response.data.hours_remaining
        }));
        
        // Now fetch the viewers list
        const viewersResponse = await axios.get(`${API}/profile/views/list`);
        setViewers(viewersResponse.data.viewers || []);
        toast.success('Viewers unlocked! See who\'s been checking you out.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock viewers');
    } finally {
      setViewersLoading(false);
    }
  };

  const handleOpenViewersModal = async () => {
    setShowViewersModal(true);
    if (viewsData.is_unlocked) {
      // Already unlocked, fetch viewers
      setViewersLoading(true);
      try {
        const response = await axios.get(`${API}/profile/views/list`);
        setViewers(response.data.viewers || []);
      } catch (error) {
        console.error("Error fetching viewers:", error);
      } finally {
        setViewersLoading(false);
      }
    }
  };

  const handleBoostProfile = async () => {
    if (userCoins < COIN_COSTS.BOOST_PROFILE) {
      setShowBoostModal(false);
      setNeededCoins(COIN_COSTS.BOOST_PROFILE - userCoins);
      setShowEarnCoins(true);
      return;
    }
    
    setBoostLoading(true);
    try {
      const response = await axios.post(`${API}/profile/boost`);
      if (response.data.success) {
        setUserCoins(response.data.new_balance);
        setBoostStatus({
          is_boosted: true,
          boost_available: false,
          hours_remaining: 24,
          expires_at: response.data.expires_at
        });
        setShowBoostModal(false);
        toast.success('Your profile is now boosted! You\'ll appear at the top of Discover for 24 hours.');
      } else if (response.data.already_boosted) {
        setBoostStatus({
          is_boosted: true,
          boost_available: false,
          hours_remaining: response.data.hours_remaining,
          expires_at: response.data.expires_at
        });
        setShowBoostModal(false);
        toast.info(`Your profile is already boosted for ${response.data.hours_remaining.toFixed(1)} more hours.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to boost profile');
    } finally {
      setBoostLoading(false);
    }
  };

  const handleUnlockSession = async () => {
    if (userCoins < COIN_COSTS.UNLOCK_AREA_SESSION) {
      setShowAreaUnlock(false);
      setNeededCoins(COIN_COSTS.UNLOCK_AREA_SESSION - userCoins);
      setShowEarnCoins(true);
      return;
    }
    
    try {
      await axios.post(`${API}/golf-coins/spend`, {
        amount: COIN_COSTS.UNLOCK_AREA_SESSION,
        reason: 'discovery_session_unlock',
        description: 'Unlocked golfer discovery for this session'
      });
      
      setUserCoins(prev => prev - COIN_COSTS.UNLOCK_AREA_SESSION);
      setSessionUnlocked(true);
      sessionStorage.setItem('discovery_session_unlocked', 'true');
      setShowAreaUnlock(false);
      toast.success('Discovery unlocked! Find golfers in your area.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock');
    }
  };

  const handleUnlockProfile = async (golfer) => {
    // Check if user can afford first
    if (userCoins < COIN_COSTS.VIEW_PROFILE) {
      setNeededCoins(COIN_COSTS.VIEW_PROFILE - userCoins);
      setShowEarnCoins(true);
      return;
    }
    
    // Silent deduction - no popup confirmation
    try {
      // Send connection request (this deducts coins on backend)
      const response = await axios.post(`${API}/connections/request?target_user_id=${golfer.user_id}`);
      
      if (response.data.success) {
        if (response.data.already_connected) {
          // Already connected - just show profile, no message needed
        } else if (response.data.already_pending) {
          // Already pending - just show profile
        } else {
          // New connection - silently update balance
          setUserCoins(response.data.new_balance);
        }
        
        setUnlockedProfiles(prev => new Set([...prev, golfer.user_id]));
        setSelectedGolfer(golfer);
      }
      
      // Track profile view silently
      await axios.post(`${API}/golf-coins/spend`, {
        amount: 0,  // Connection already charged
        reason: 'profile_view',
        description: `Viewed ${golfer.name}'s profile`,
        target_user_id: golfer.user_id
      }).catch(() => {});  // Ignore if this fails
      
    } catch (error) {
      // Only show error if it's not a coin issue (those show EarnCoins modal)
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('coins')) {
        setNeededCoins(COIN_COSTS.VIEW_PROFILE);
        setShowEarnCoins(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to connect');
      }
    }
  };

  const handleMessage = async (golfer) => {
    // Check if user can afford first
    if (userCoins < COIN_COSTS.SEND_MESSAGE) {
      setNeededCoins(COIN_COSTS.SEND_MESSAGE - userCoins);
      setShowEarnCoins(true);
      return;
    }
    
    // Silent deduction - no popup
    try {
      await axios.post(`${API}/golf-coins/spend`, {
        amount: COIN_COSTS.SEND_MESSAGE,
        reason: 'send_message',
        description: `Messaged ${golfer.name}`,
        target_user_id: golfer.user_id
      });
      
      setUserCoins(prev => prev - COIN_COSTS.SEND_MESSAGE);
      navigate(`/chat?user=${golfer.user_id}`);
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('coins')) {
        setNeededCoins(COIN_COSTS.SEND_MESSAGE);
        setShowEarnCoins(true);
      } else {
        toast.error('Failed to start conversation');
      }
    }
  };

  const handleBuddyRequest = async (golfer) => {
    // Check if user can afford first
    if (userCoins < COIN_COSTS.SEND_BUDDY_REQUEST) {
      setNeededCoins(COIN_COSTS.SEND_BUDDY_REQUEST - userCoins);
      setShowEarnCoins(true);
      return;
    }
    
    // Silent deduction - no popup
    try {
      await axios.post(`${API}/golf-coins/spend`, {
        amount: COIN_COSTS.SEND_BUDDY_REQUEST,
        reason: 'buddy_request',
        description: `Buddy request to ${golfer.name}`,
        target_user_id: golfer.user_id
      });
      
      await axios.post(`${API}/golf-buddies/requests`, {
        target_user_id: golfer.user_id
      });
      
      setUserCoins(prev => prev - COIN_COSTS.SEND_BUDDY_REQUEST);
      // Silent success - no toast
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('coins')) {
        setNeededCoins(COIN_COSTS.SEND_BUDDY_REQUEST);
        setShowEarnCoins(true);
      }
      // Silent fail for other errors
    }
  };

  // Show area unlock gate if session not unlocked
  if (!sessionUnlocked && !loading) {
    return (
      <div className="ps-theme-page min-h-screen flex flex-col" data-testid="discover-locked-page">
        {/* Header with Back Button */}
        <div className="ps-header-bar sticky top-0 z-40 backdrop-blur-sm border-b">
          <div className="flex items-center justify-between p-4">
            <button 
              onClick={() => navigate('/home')}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="discovery-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/30">
              <Coins className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 font-bold">{userCoins}</span>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="ps-page-header pt-4 pb-8 px-4">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-500/20 border-2 border-emerald-500/30 flex items-center justify-center">
              <Eye className="w-10 h-10 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">PS Golfers</h1>
            <p className="text-gray-400 mb-6">
              {golfers.length} golfers in your area are waiting to connect
            </p>
            
            {/* Blurred Preview */}
            <div className="relative mb-6">
              <div className="grid grid-cols-3 gap-2 opacity-50">
                {[1,2,3].map((i) => (
                  <div key={i} className="aspect-square rounded-xl bg-gray-800" style={{ filter: 'blur(8px)' }} />
                ))}
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Lock className="w-12 h-12 text-white/50" />
              </div>
            </div>
            
            {/* Unlock Button */}
            <Button
              onClick={() => setShowAreaUnlock(true)}
              className="w-full h-14 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-lg font-semibold rounded-xl"
              data-testid="unlock-discovery-btn"
            >
              <Lock className="w-5 h-5 mr-2" />
              Unlock Discovery
            </Button>
            
            <p className="text-gray-500 text-sm mt-3">
              Your balance: <span className="text-amber-400 font-semibold">{userCoins} coins</span>
            </p>
          </div>
        </div>
        
        {/* Teaser Stats */}
        <div className="px-4 py-6">
          <div className="max-w-lg mx-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-emerald-400">{golfers.length || 15}</p>
                <p className="text-gray-400 text-sm">Golfers nearby</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-emerald-400">{golfers.filter(g => g.is_online).length || 5}</p>
                <p className="text-gray-400 text-sm">Online now</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Area Unlock Modal */}
        <CoinGateModal
          isOpen={showAreaUnlock}
          onClose={() => setShowAreaUnlock(false)}
          action="Unlock golfer discovery for this session"
          cost={COIN_COSTS.UNLOCK_AREA_SESSION}
          userCoins={userCoins}
          onConfirm={handleUnlockSession}
          onShowEarnCoins={() => {
            setShowAreaUnlock(false);
            setNeededCoins(COIN_COSTS.UNLOCK_AREA_SESSION - userCoins);
            setShowEarnCoins(true);
          }}
        />
        
        {/* Earn Coins Modal */}
        <EarnCoinsModal
          isOpen={showEarnCoins}
          onClose={() => setShowEarnCoins(false)}
          userCoins={userCoins}
          neededCoins={neededCoins}
          onNavigate={(path) => {
            setShowEarnCoins(false);
            navigate(path);
          }}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="ps-theme-page min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Handle background tap to dismiss carousel and go home
  const handleBackgroundTap = (e) => {
    // Only trigger if clicking directly on the background container, not on child elements
    if (e.target === e.currentTarget) {
      navigate('/home');
    }
  };

  // Full Profile View - when user taps on a profile
  if (selectedProfile) {
    const golfer = selectedProfile;
    const photos = golfer.photos?.length > 0 ? golfer.photos : (golfer.picture ? [golfer.picture] : []);
    const initials = golfer.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'G';
    
    return (
      <div className="ps-theme-page min-h-screen" data-testid="full-profile-view">
        {/* Header with Back Button */}
        <div className="ps-header-bar sticky top-0 z-40 backdrop-blur-sm border-b">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setSelectedProfile(null)}
                className="p-2 rounded-full bg-gray-800 text-gray-400 hover:text-white transition-colors"
                data-testid="profile-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-white">{golfer.name}</h1>
                <p className="text-xs text-gray-400">@{golfer.username || 'golfer'}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/30">
              <span data-testid={`discovery-handicap-${golfer.user_id}`} className="text-emerald-400 font-bold">{typeof golfer.handicap === 'number' && Number.isFinite(golfer.handicap) ? golfer.handicap.toFixed(1) : 'Not established'}</span>
              <span className="text-emerald-400/60 text-xs">HCP</span>
            </div>
          </div>
        </div>
        
        {/* Profile Content */}
        <div className="pb-24">
          {/* Main Photo */}
          <div className="ps-fixed-contrast relative h-80">
            {photos.length > 0 ? (
              <img 
                src={photos[0]} 
                alt={golfer.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-emerald-900/50 to-gray-900 flex items-center justify-center">
                <div className="w-32 h-32 rounded-full bg-emerald-500/20 border-2 border-emerald-500/30 flex items-center justify-center">
                  <span className="text-5xl font-bold text-emerald-400/60">{initials}</span>
                </div>
              </div>
            )}
            
            {/* Online Badge */}
            {golfer.is_online && (
              <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-emerald-500 text-white text-sm flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                Online now
              </div>
            )}
          </div>
          
          {/* Profile Details */}
          <div className="p-4 space-y-4">
            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                <MapPin className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                <p className="text-white font-medium text-sm">{golfer.location || golfer.city || 'Local'}</p>
                <p className={`text-xs ${golfer.distance_accurate ? 'text-emerald-400' : golfer.distance === '?' ? 'text-gray-600' : 'text-gray-400'}`}>
                  {golfer.distance === '?' ? 'Distance unknown' : `${golfer.distance} mi away`}
                  {golfer.distance_accurate && <Navigation className="w-3 h-3 inline ml-1" />}
                </p>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                <Flag className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                <p className="text-white font-medium text-sm">{golfer.rounds_played || 0}</p>
                <p className="text-gray-500 text-xs">Rounds Played</p>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                <Calendar className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                <p className="text-white font-medium text-sm capitalize">{golfer.availability || 'Flexible'}</p>
                <p className="text-gray-500 text-xs">Availability</p>
              </div>
            </div>
            
            {/* Home Course */}
            {golfer.home_course && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                <p className="text-emerald-400 text-sm font-medium mb-1">Home Course</p>
                <p className="text-white text-lg">{golfer.home_course}</p>
              </div>
            )}
            
            {/* Bio */}
            {golfer.bio && (
              <div className="bg-gray-800/50 rounded-xl p-4">
                <p className="text-gray-400 text-sm font-medium mb-2">About</p>
                <p className="text-white">{golfer.bio}</p>
              </div>
            )}
            
            {/* Looking For */}
            {golfer.looking_for && (
              <div className="bg-pink-500/10 border border-pink-500/20 rounded-xl p-4">
                <p className="text-pink-400 text-sm font-medium mb-1">Looking For</p>
                <p className="text-gray-300">{golfer.looking_for}</p>
              </div>
            )}
            
            {/* Play Style & Skill Tags */}
            {(golfer.play_style || golfer.skill_level) && (
              <div className="flex flex-wrap gap-2">
                {golfer.play_style && (
                  <span className="px-4 py-2 rounded-full bg-gray-800 text-gray-300">
                    {golfer.play_style}
                  </span>
                )}
                {golfer.skill_level && (
                  <span className="px-4 py-2 rounded-full bg-gray-800 text-gray-300">
                    {golfer.skill_level}
                  </span>
                )}
              </div>
            )}
            
            {/* Message Button */}
            <Button
              onClick={() => handleMessage(golfer)}
              className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white py-6 text-lg"
              data-testid="message-golfer-btn"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Message {golfer.name?.split(' ')[0]}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="ps-theme-page min-h-screen pb-24"
      onClick={handleBackgroundTap}
      data-testid="ps-members-page"
    >  {/* Header with Back Button and Filters */}
      <div className="ps-header-bar sticky top-0 z-40 backdrop-blur-sm border-b">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/home')}
              className="p-2 rounded-full bg-gray-800 text-gray-400 hover:text-white transition-colors"
              data-testid="discovery-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                PS Golfers
                {isTracking && <LocationStatusIndicator />}
              </h1>
              <p className="text-xs text-gray-400">Find golfers nearby · Chat · Build your crew</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-full transition-colors ${showFilters ? 'bg-emerald-500 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
              data-testid="filter-toggle-btn"
            >
              <SlidersHorizontal className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/30">
              <Coins className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 font-bold">{userCoins}</span>
            </div>
          </div>
        </div>
        
        {/* Filter Panel */}
        {showFilters && (
          <div className="px-4 pb-4 border-t border-gray-800 pt-3 animate-in slide-in-from-top-2">
            <div className="max-w-lg mx-auto">
              <div className="grid grid-cols-2 gap-3 mb-3">
                {/* Handicap Filter */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Handicap</label>
                  <select
                    value={filterHandicap}
                    onChange={(e) => setFilterHandicap(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none"
                    data-testid="filter-handicap"
                  >
                    <option value="all">Any</option>
                    <option value="scratch">Scratch (0-5)</option>
                    <option value="low">Low (6-12)</option>
                    <option value="mid">Mid (13-20)</option>
                    <option value="high">High (21+)</option>
                  </select>
                </div>
                
                {/* Distance Filter */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Distance</label>
                  <select
                    value={filterDistance}
                    onChange={(e) => setFilterDistance(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none"
                    data-testid="filter-distance"
                  >
                    <option value="all">Any</option>
                    <option value="10">Within 10 mi</option>
                    <option value="25">Within 25 mi</option>
                    <option value="50">Within 50 mi</option>
                    <option value="100">Within 100 mi</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {/* Age Filter */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Age Range</label>
                  <select
                    value={filterAge}
                    onChange={(e) => setFilterAge(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none"
                    data-testid="filter-age"
                  >
                    <option value="all">Any Age</option>
                    <option value="18-29">18-29</option>
                    <option value="30-39">30-39</option>
                    <option value="40-49">40-49</option>
                    <option value="50-59">50-59</option>
                    <option value="60+">60+</option>
                  </select>
                </div>
                
                {/* Gender Filter */}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Gender</label>
                  <select
                    value={filterGender}
                    onChange={(e) => setFilterGender(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none"
                    data-testid="filter-gender"
                  >
                    <option value="all">Any</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>
              
              {/* Search Button - applies filters and closes panel */}
              <Button
                onClick={() => setShowFilters(false)}
                className="w-full mt-4 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold"
                data-testid="filter-search-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                Search Golfers
              </Button>
              
              {/* Active Filters Count */}
              {(filterHandicap !== 'all' || filterDistance !== 'all' || filterAge !== 'all' || filterGender !== 'all') && (
                <div className="flex items-center justify-between mt-3">
                  <p className="text-sm text-gray-400">
                    {filteredGolfers.length} golfers match
                  </p>
                  <button
                    onClick={() => {
                      setFilterHandicap('all');
                      setFilterDistance('all');
                      setFilterAge('all');
                      setFilterGender('all');
                    }}
                    className="text-sm text-emerald-400 hover:text-emerald-300"
                    data-testid="clear-filters-btn"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Golfer Grid */}
      <div className="p-4">
        <div className="max-w-lg mx-auto space-y-4">
          {/* Location Permission Banner */}
          {showLocationBanner && !hasLocation && !isTracking && (
            <LocationPermissionBanner onClose={() => setShowLocationBanner(false)} />
          )}
          <LocationDeniedBanner />
          
          {/* Profile Carousel - Now at the top */}
          {filteredGolfers.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500">No golfers found</p>
              <p className="text-gray-600 text-sm">
                {golfers.length > 0 ? 'Try adjusting your filters' : 'Try expanding your search radius'}
              </p>
              {golfers.length > 0 && (filterHandicap !== 'all' || filterDistance !== 'all' || filterAge !== 'all' || filterGender !== 'all') && (
                <Button
                  onClick={() => {
                    setFilterHandicap('all');
                    setFilterDistance('all');
                    setFilterAge('all');
                    setFilterGender('all');
                  }}
                  variant="outline"
                  className="mt-4 border-emerald-500/50 text-emerald-400"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Tap area above carousel to go home */}
              <div 
                className="h-6 -mt-2 cursor-pointer"
                onClick={() => navigate('/home')}
                data-testid="tap-dismiss-top"
              />
              <ProfileCarousel
                golfers={filteredGolfers}
                onViewProfile={setSelectedProfile}
                onDismiss={() => navigate('/home')}
              />
            </>
          )}
          
          {/* Options Cards - Now below carousel */}
          {/* Boost Your Profile Card */}
          <div className={`relative overflow-hidden rounded-2xl border ${boostStatus.is_boosted ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/50' : 'bg-gradient-to-br from-gray-900 to-gray-800 border-gray-700'}`}>
            <div className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${boostStatus.is_boosted ? 'bg-amber-500/30' : 'bg-amber-500/10'}`}>
                  <Rocket className={`w-6 h-6 ${boostStatus.is_boosted ? 'text-amber-400' : 'text-amber-500/70'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold text-base">
                    {boostStatus.is_boosted ? 'Profile Boosted!' : 'Boost Your Profile'}
                  </h3>
                  <p className="text-gray-500 text-xs">
                    {boostStatus.is_boosted 
                      ? `Active for ${boostStatus.hours_remaining?.toFixed(1)} more hours`
                      : 'Get seen first · More connections · Stand out'
                    }
                  </p>
                </div>
                {boostStatus.is_boosted ? (
                  <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/20 border border-amber-500/30">
                    <Zap className="w-3 h-3 text-amber-400 fill-amber-400" />
                    <span className="text-amber-400 font-bold text-xs">ACTIVE</span>
                  </div>
                ) : (
                  <Button
                    onClick={() => setShowBoostModal(true)}
                    size="sm"
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold"
                    data-testid="boost-profile-btn"
                  >
                    <Zap className="w-3 h-3 mr-1" />
                    Boost
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          {/* Who Viewed You Card */}
          <div className={`relative overflow-hidden rounded-2xl border ${viewsData.is_unlocked ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/50' : 'bg-gradient-to-br from-gray-900 to-gray-800 border-gray-700'}`}>
            <div className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${viewsData.is_unlocked ? 'bg-purple-500/30' : 'bg-purple-500/10'}`}>
                  <Eye className={`w-6 h-6 ${viewsData.is_unlocked ? 'text-purple-400' : 'text-purple-500/70'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold text-base">Who Viewed You</h3>
                  <p className="text-gray-500 text-xs">
                    {viewsData.view_count > 0 
                      ? `${viewsData.view_count} golfer${viewsData.view_count !== 1 ? 's' : ''} checked you out`
                      : 'See who\'s interested in your game'
                    }
                  </p>
                </div>
                {viewsData.is_unlocked ? (
                  <Button
                    onClick={handleOpenViewersModal}
                    size="sm"
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold"
                    data-testid="view-viewers-btn"
                  >
                    <Users className="w-3 h-3 mr-1" />
                    View
                  </Button>
                ) : (
                  <Button
                    onClick={handleOpenViewersModal}
                    size="sm"
                    disabled={viewsData.view_count === 0}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold disabled:opacity-50"
                    data-testid="unlock-viewers-btn"
                  >
                    <Lock className="w-3 h-3 mr-1" />
                    Unlock
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          {/* Likes You Card */}
          <div className={`relative overflow-hidden rounded-2xl border ${likesData.is_unlocked ? 'bg-gradient-to-br from-pink-500/20 to-rose-500/20 border-pink-500/50' : 'bg-gradient-to-br from-gray-900 to-gray-800 border-gray-700'}`}>
            <div className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${likesData.is_unlocked ? 'bg-pink-500/30' : 'bg-pink-500/10'}`}>
                  <span className="text-xl">⛳️</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold text-base">Likes You</h3>
                  <p className="text-gray-500 text-xs">
                    {likesData.like_count > 0 
                      ? `${likesData.like_count} golfer${likesData.like_count !== 1 ? 's' : ''} want to connect`
                      : 'Match with golfers who like your style'
                    }
                  </p>
                </div>
                {likesData.is_unlocked ? (
                  <Button
                    onClick={handleOpenLikesModal}
                    size="sm"
                    className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-semibold"
                    data-testid="view-likes-btn"
                  >
                    <span className="mr-1">⛳️</span>
                    View
                  </Button>
                ) : (
                  <Button
                    onClick={handleOpenLikesModal}
                    size="sm"
                    disabled={likesData.like_count === 0}
                    className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-semibold disabled:opacity-50"
                    data-testid="unlock-likes-btn"
                  >
                    <Lock className="w-3 h-3 mr-1" />
                    Unlock
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Boost Profile Modal */}
      <Dialog open={showBoostModal} onOpenChange={setShowBoostModal}>
        <DialogContent className="ps-themed-dialog ps-theme-panel max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white text-center flex items-center justify-center gap-2">
              <Rocket className="w-6 h-6 text-amber-400" />
              Boost Your Profile
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {/* Visual Preview */}
            <div className="relative mb-6">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 text-center">
                <Zap className="w-12 h-12 text-white mx-auto mb-2 fill-white" />
                <p className="text-white font-bold text-lg">24 Hours at the Top</p>
                <p className="text-white/80 text-sm">Get 10x more profile views</p>
              </div>
            </div>
            
            {/* Cost */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <Coins className="w-8 h-8 text-amber-400" />
              <span className="text-3xl font-bold text-amber-400">{COIN_COSTS.BOOST_PROFILE}</span>
              <span className="text-gray-400">coins</span>
            </div>
            
            {/* Balance Check */}
            <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg mb-4">
              <span className="text-gray-400">Your balance</span>
              <span className={`font-bold ${userCoins >= COIN_COSTS.BOOST_PROFILE ? 'text-emerald-400' : 'text-red-400'}`}>
                {userCoins} coins
              </span>
            </div>
            
            {userCoins < COIN_COSTS.BOOST_PROFILE && (
              <p className="text-center text-red-400 text-sm mb-4">
                You need {COIN_COSTS.BOOST_PROFILE - userCoins} more coins
              </p>
            )}
            
            {/* Benefits List */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-3 text-gray-300">
                <Sparkles className="w-5 h-5 text-amber-400" />
                <span className="text-sm">Appear first in Discover feed</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <Eye className="w-5 h-5 text-amber-400" />
                <span className="text-sm">Golden "Boosted" badge on your profile</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <Users className="w-5 h-5 text-amber-400" />
                <span className="text-sm">Attract more golf buddies</span>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-3">
            <Button variant="outline" onClick={() => setShowBoostModal(false)} className="flex-1 border-gray-700 text-gray-400">
              Cancel
            </Button>
            {userCoins >= COIN_COSTS.BOOST_PROFILE ? (
              <Button 
                onClick={handleBoostProfile}
                disabled={boostLoading}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white"
              >
                {boostLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Boost Now
                  </>
                )}
              </Button>
            ) : (
              <Button 
                onClick={() => {
                  setShowBoostModal(false);
                  setNeededCoins(COIN_COSTS.BOOST_PROFILE - userCoins);
                  setShowEarnCoins(true);
                }}
                className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 text-white"
              >
                <Coins className="w-4 h-4 mr-2" />
                Get Coins
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Who Viewed You Modal */}
      <Dialog open={showViewersModal} onOpenChange={setShowViewersModal}>
        <DialogContent className="ps-themed-dialog ps-theme-panel max-w-sm max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white text-center flex items-center justify-center gap-2">
              <Eye className="w-6 h-6 text-purple-400" />
              Who Viewed You
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 flex-1 overflow-hidden flex flex-col">
            {viewsData.is_unlocked ? (
              // Show viewers list
              <div className="flex-1 overflow-auto">
                {viewersLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : viewers.length > 0 ? (
                  <div className="space-y-3">
                    {viewers.map((viewer) => (
                      <div 
                        key={viewer.user_id}
                        className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-xl hover:bg-gray-800 transition-colors cursor-pointer"
                        onClick={() => {
                          setShowViewersModal(false);
                          navigate(`/profile/${viewer.user_id}`);
                        }}
                      >
                        <Avatar className="w-12 h-12 border-2 border-purple-500/30">
                          <AvatarImage src={viewer.picture} alt={viewer.name} />
                          <AvatarFallback className="bg-purple-500/20 text-purple-400">
                            {viewer.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'G'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="text-white font-medium">{viewer.name}</p>
                          <div className="flex items-center gap-2 text-gray-400 text-sm">
                            <MapPin className="w-3 h-3" />
                            <span>{viewer.location || viewer.city || 'Unknown'}</span>
                            {viewer.handicap != null && (
                              <>
                                <span>•</span>
                                <span>{viewer.handicap.toFixed(1)} HCP</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-purple-400 text-xs">
                            {viewer.view_count > 1 ? `${viewer.view_count}x` : 'Viewed'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Eye className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-500">No viewers yet</p>
                    <p className="text-gray-600 text-sm">Boost your profile to get noticed!</p>
                  </div>
                )}
              </div>
            ) : (
              // Show unlock prompt
              <>
                <div className="relative mb-6">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-4 text-center">
                    <Eye className="w-12 h-12 text-white mx-auto mb-2" />
                    <p className="text-white font-bold text-lg">See Your Admirers</p>
                    <p className="text-white/80 text-sm">{viewsData.view_count} golfer{viewsData.view_count !== 1 ? 's' : ''} checked you out</p>
                  </div>
                </div>
                
                {/* Cost */}
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Coins className="w-8 h-8 text-purple-400" />
                  <span className="text-3xl font-bold text-purple-400">{COIN_COSTS.SEE_WHO_VIEWED}</span>
                  <span className="text-gray-400">coins</span>
                </div>
                
                {/* Balance Check */}
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg mb-4">
                  <span className="text-gray-400">Your balance</span>
                  <span className={`font-bold ${userCoins >= COIN_COSTS.SEE_WHO_VIEWED ? 'text-emerald-400' : 'text-red-400'}`}>
                    {userCoins} coins
                  </span>
                </div>
                
                {userCoins < COIN_COSTS.SEE_WHO_VIEWED && (
                  <p className="text-center text-red-400 text-sm mb-4">
                    You need {COIN_COSTS.SEE_WHO_VIEWED - userCoins} more coins
                  </p>
                )}
                
                {/* Benefits */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-3 text-gray-300">
                    <Users className="w-5 h-5 text-purple-400" />
                    <span className="text-sm">See all {viewsData.view_count} viewers</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Clock className="w-5 h-5 text-purple-400" />
                    <span className="text-sm">24 hours access</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <MessageCircle className="w-5 h-5 text-purple-400" />
                    <span className="text-sm">Connect with interested golfers</span>
                  </div>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter className="flex gap-3">
            <Button variant="outline" onClick={() => setShowViewersModal(false)} className="flex-1 border-gray-700 text-gray-400">
              {viewsData.is_unlocked ? 'Close' : 'Cancel'}
            </Button>
            {!viewsData.is_unlocked && (
              userCoins >= COIN_COSTS.SEE_WHO_VIEWED ? (
                <Button 
                  onClick={handleUnlockViewers}
                  disabled={viewersLoading}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                >
                  {viewersLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Unlock Now
                    </>
                  )}
                </Button>
              ) : (
                <Button 
                  onClick={() => {
                    setShowViewersModal(false);
                    setNeededCoins(COIN_COSTS.SEE_WHO_VIEWED - userCoins);
                    setShowEarnCoins(true);
                  }}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600 text-white"
                >
                  <Coins className="w-4 h-4 mr-2" />
                  Get Coins
                </Button>
              )
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Coin Gate Modal */}
      <CoinGateModal
        isOpen={coinModal.isOpen}
        onClose={() => setCoinModal({ isOpen: false })}
        action={coinModal.action}
        cost={coinModal.cost}
        userCoins={userCoins}
        onConfirm={coinModal.onConfirm}
        onShowEarnCoins={() => {
          setCoinModal({ isOpen: false });
          setNeededCoins(coinModal.cost - userCoins);
          setShowEarnCoins(true);
        }}
      />
      
      {/* Likes You Modal */}
      <Dialog open={showLikesModal} onOpenChange={setShowLikesModal}>
        <DialogContent className="ps-themed-dialog ps-theme-panel max-w-sm max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white text-center flex items-center justify-center gap-2">
              <span className="text-xl">⛳️</span>
              Likes You
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 flex-1 overflow-hidden flex flex-col">
            {likesData.is_unlocked ? (
              // Show likers list
              <div className="flex-1 overflow-auto">
                {likersLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : likers.length > 0 ? (
                  <div className="space-y-3">
                    {likers.map((liker) => (
                      <div 
                        key={liker.user_id}
                        className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-xl hover:bg-gray-800 transition-colors cursor-pointer"
                        onClick={() => {
                          setShowLikesModal(false);
                          navigate(`/profile/${liker.user_id}`);
                        }}
                      >
                        <div className="relative">
                          <Avatar className="w-12 h-12 border-2 border-pink-500/30">
                            <AvatarImage src={liker.picture} alt={liker.name} />
                            <AvatarFallback className="bg-pink-500/20 text-pink-400">
                              {liker.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'G'}
                            </AvatarFallback>
                          </Avatar>
                          {liker.is_super && (
                            <Star className="absolute -top-1 -right-1 w-5 h-5 text-yellow-400 fill-yellow-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-medium">{liker.name}</p>
                          <div className="flex items-center gap-2 text-gray-400 text-sm">
                            <MapPin className="w-3 h-3" />
                            <span>{liker.location || liker.city || 'Unknown'}</span>
                            {liker.handicap != null && (
                              <>
                                <span>•</span>
                                <span>{liker.handicap.toFixed(1)} HCP</span>
                              </>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLike({ user_id: liker.user_id, name: liker.name }, false);
                          }}
                          className="bg-gradient-to-r from-pink-500 to-rose-500 text-white"
                        >
                          <span className="mr-1">⛳️</span>
                          Like Back
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <span className="text-5xl block mx-auto mb-3">⛳️</span>
                    <p className="text-gray-500">No likes yet</p>
                    <p className="text-gray-600 text-sm">Be more active to get noticed!</p>
                  </div>
                )}
              </div>
            ) : (
              // Show unlock prompt
              <>
                <div className="relative mb-6">
                  <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-xl p-4 text-center">
                    <span className="text-5xl block mx-auto mb-2">⛳️</span>
                    <p className="text-white font-bold text-lg">See Who Likes You</p>
                    <p className="text-white/80 text-sm">{likesData.like_count} golfer{likesData.like_count !== 1 ? 's' : ''} interested in you</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Coins className="w-8 h-8 text-pink-400" />
                  <span className="text-3xl font-bold text-pink-400">{COIN_COSTS.SEE_LIKES}</span>
                  <span className="text-gray-400">coins</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg mb-4">
                  <span className="text-gray-400">Your balance</span>
                  <span className={`font-bold ${userCoins >= COIN_COSTS.SEE_LIKES ? 'text-emerald-400' : 'text-red-400'}`}>
                    {userCoins} coins
                  </span>
                </div>
                
                {userCoins < COIN_COSTS.SEE_LIKES && (
                  <p className="text-center text-red-400 text-sm mb-4">
                    You need {COIN_COSTS.SEE_LIKES - userCoins} more coins
                  </p>
                )}
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-3 text-gray-300">
                    <span>⛳️</span>
                    <span className="text-sm">See all {likesData.like_count} people who liked you</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    <span className="text-sm">{likesData.super_like_count} Super Likes included</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-pink-400" />
                    <span className="text-sm">Like them back to match instantly</span>
                  </div>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter className="flex gap-3">
            <Button variant="outline" onClick={() => setShowLikesModal(false)} className="flex-1 border-gray-700 text-gray-400">
              {likesData.is_unlocked ? 'Close' : 'Cancel'}
            </Button>
            {!likesData.is_unlocked && (
              userCoins >= COIN_COSTS.SEE_LIKES ? (
                <Button 
                  onClick={handleUnlockLikes}
                  disabled={likersLoading}
                  className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 text-white"
                >
                  {likersLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Unlock Now
                    </>
                  )}
                </Button>
              ) : (
                <Button 
                  onClick={() => {
                    setShowLikesModal(false);
                    setNeededCoins(COIN_COSTS.SEE_LIKES - userCoins);
                    setShowEarnCoins(true);
                  }}
                  className="flex-1 bg-gradient-to-r from-pink-500 to-pink-600 text-white"
                >
                  <Coins className="w-4 h-4 mr-2" />
                  Get Coins
                </Button>
              )
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Match Celebration Modal */}
      <Dialog open={showMatchModal} onOpenChange={setShowMatchModal}>
        <DialogContent className="ps-themed-dialog ps-theme-panel max-w-sm text-center">
          <div className="py-6">
            <div className="relative mb-6">
              <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center animate-pulse">
                <span className="text-5xl">⛳️</span>
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center animate-bounce">
                <Star className="w-5 h-5 text-yellow-900 fill-yellow-900" />
              </div>
            </div>
            
            <h2 className="text-3xl font-bold text-white mb-2">It's a Match!</h2>
            <p className="text-gray-400 mb-6">
              You and <span className="text-pink-400 font-semibold">{matchedUser?.name}</span> liked each other
            </p>
            
            {matchedUser && (
              <Avatar className="w-20 h-20 mx-auto mb-6 border-4 border-pink-500">
                <AvatarImage src={matchedUser.picture} alt={matchedUser.name} />
                <AvatarFallback className="bg-pink-500/20 text-pink-400 text-2xl">
                  {matchedUser.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'G'}
                </AvatarFallback>
              </Avatar>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowMatchModal(false)}
                className="flex-1 border-gray-700 text-gray-400"
              >
                Keep Browsing
              </Button>
              <Button
                onClick={() => {
                  setShowMatchModal(false);
                  if (matchedUser?.user_id) {
                    navigate(`/chat?user=${matchedUser.user_id}`);
                  }
                }}
                className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 text-white"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Send Message
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Earn Coins Modal */}
      <EarnCoinsModal
        isOpen={showEarnCoins}
        onClose={() => setShowEarnCoins(false)}
        userCoins={userCoins}
        neededCoins={neededCoins}
        onNavigate={(path) => {
          setShowEarnCoins(false);
          navigate(path);
        }}
      />
    </div>
  );
};

export default GolferDiscovery;
